import {
  AppMessage,
  DevMessage,
  Triggers,
  ImpedanceExec,
  ImpedanceResult,
  ImpedanceData,
  State,
  Version,
  PowerState,
  ElectrodeDetect,
  CHAN,
  TIA,
  tIAToNumber,
  ADC,
  Scaler,
} from './generated/ediphor';
import { appendCRC32, verifyCRC32 } from './crc32';
import logger from '@/lib/logger';

// ============ Framing Constants ============

/**
 * Frame synchronization bytes
 * Frame structure: [0xAA][0x55][Size_Low][Size_High][Payload]
 * Payload = Protobuf Data + CRC32 (4 bytes)
 * Size = Length of Payload (2 bytes, little-endian)
 */
export const SYNC_BYTE_1 = 0xaa;
export const SYNC_BYTE_2 = 0x55;
export const HEADER_SIZE = 4; // 2 sync bytes + 2 size bytes
/** Largest payload (protobuf + CRC32) the 2-byte size field can declare. */
export const MAX_PAYLOAD_SIZE = 0xffff;

/**
 * Wrap a payload (protobuf + CRC32) in the frame header: [0xAA][0x55][size LE u16][payload].
 * Inverse of what `FrameAssembler` strips off.
 */
export function frameBytes(payload: Uint8Array): Uint8Array {
  if (payload.length > MAX_PAYLOAD_SIZE) {
    throw new Error(`Payload size ${payload.length} exceeds maximum frame size (${MAX_PAYLOAD_SIZE} bytes)`);
  }
  const frame = new Uint8Array(HEADER_SIZE + payload.length);
  frame[0] = SYNC_BYTE_1;
  frame[1] = SYNC_BYTE_2;
  frame[2] = payload.length & 0xff;
  frame[3] = (payload.length >>> 8) & 0xff;
  frame.set(payload, HEADER_SIZE);
  return frame;
}

// ============ Encoding Functions ============

/**
 * Encodes an AppMessage to Uint8Array with framing, CRC32 for sending via WebUSB
 * 
 * Frame structure:
 * ┌──────┬──────┬─────────────────┬──────────────────────┬──────────┐
 * │ 0xAA │ 0x55 │  Size (2 bytes) │  Protobuf Data       │  CRC32   │
 * │      │      │  Little-Endian  │                      │ (4 bytes)│
 * └──────┴──────┴─────────────────┴──────────────────────┴──────────┘
 * 
 * CRC32 is calculated over: [Protobuf Data] only (NOT including header)
 * 
 * @param message - The AppMessage to encode
 * @returns Framed bytes with header, protobuf data, and CRC32, ready for USB transfer
 */
export function encodeAppMessage(message: AppMessage): Uint8Array {
  // Encode protobuf message
  let protobufBytes = AppMessage.encode(message).finish();

  // Proto3 suppresses fields at their default value (0).
  // Triggers.NONE = 0 is the proto3 default, so the generated encoder never
  // writes it. The device firmware requires trig to always be explicitly
  // present on the wire. Inject the field manually when it would be omitted.
  //
  // Protobuf binary: field_number=3, wire_type=0(varint) → tag = (3<<3)|0 = 0x18
  //                  value 0 → varint 0x00
  if (message.trig === Triggers.NONE) {
    const trigField = new Uint8Array([0x18, 0x00]);
    const withTrig = new Uint8Array(trigField.length + protobufBytes.length);
    withTrig.set(trigField, 0);
    withTrig.set(protobufBytes, trigField.length);
    protobufBytes = withTrig;
  }

  // Append CRC32 to protobuf data (this is the payload)
  const payload = appendCRC32(protobufBytes);
  
  // Log for debugging
  const crcBytes = payload.slice(-4);
  logger.debug('CRC32 bytes:', Array.from(crcBytes).map(b => '0x' + b.toString(16).padStart(2, '0').toUpperCase()).join(' '));
  
  // Calculate payload size (protobuf + CRC32)
  const payloadSize = payload.length;
  
  // Validate size fits in 2 bytes (max 65535)
  if (payloadSize > 0xffff) {
    throw new Error(`Payload size ${payloadSize} exceeds maximum frame size (65535 bytes)`);
  }
  
  // Create framed message: [Sync1][Sync2][Size_Low][Size_High][Payload]
  const frame = new Uint8Array(HEADER_SIZE + payloadSize);
  
  // Write sync bytes
  frame[0] = SYNC_BYTE_1;
  frame[1] = SYNC_BYTE_2;
  
  // Write size as 2-byte little-endian
  frame[2] = payloadSize & 0xff;         // Low byte
  frame[3] = (payloadSize >>> 8) & 0xff; // High byte
  
  // Write payload (protobuf + CRC32)
  frame.set(payload, HEADER_SIZE);
  
  return frame;
}

/**
 * Encodes an AppMessage without CRC32 (for testing or special cases)
 * @param message - The AppMessage to encode
 * @returns Encoded protobuf bytes without CRC
 */
export function encodeAppMessageRaw(message: AppMessage): Uint8Array {
  return AppMessage.encode(message).finish();
}

/**
 * Decodes a DevMessage from Uint8Array received via WebUSB
 * 
 * Expected frame structure:
 * ┌──────┬──────┬─────────────────┬──────────────────────┬──────────┐
 * │ 0xAA │ 0x55 │  Size (2 bytes) │  Protobuf Data       │  CRC32   │
 * │      │      │  Little-Endian  │                      │ (4 bytes)│
 * └──────┴──────┴─────────────────┴──────────────────────┴──────────┘
 * 
 * CRC32 is verified over: [Protobuf Data] only (NOT including header)
 * 
 * @param data - Raw bytes from USB transfer (framed message)
 * @returns Decoded DevMessage
 * @throws Error if sync bytes invalid, size mismatch, CRC check fails, or data is malformed
 */
/**
 * Result of decoding a frame whose framing (sync bytes + declared size) was valid, whether or
 * not its CRC32 matched.
 */
export interface DevMessageDecodeResult {
  /** Decoded message — present even when `crcValid` is false, if the protobuf payload still parses. */
  message: DevMessage;
  /** Whether the frame's CRC32 matched. */
  crcValid: boolean;
  receivedCrc: number;
  calculatedCrc: number;
}

/**
 * Decode the protobuf payload that follows a validated frame header.
 *
 * Per FW-CHG-001 (protocol 1.3.0), every inbound frame — broadcast or response — is a
 * full DevMessage. A response carries `app_message`; a broadcast doesn't. There is no
 * second frame shape to disambiguate here — callers distinguish response vs. broadcast
 * by the presence of `app_message`, not by how the payload was decoded.
 */
function decodeProtobufPayload(protobufData: Uint8Array): DevMessage {
  try {
    return DevMessage.decode(protobufData);
  } catch (error) {
    throw new Error(
      `Failed to decode DevMessage: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Decode a frame's header + payload, reporting CRC validity instead of throwing on mismatch.
 * Framing errors (bad sync bytes, size mismatch, frame too short) remain hard failures — without
 * a trustworthy header there's no reliable way to locate the payload at all. A CRC mismatch,
 * by contrast, still leaves a well-located payload that may well decode correctly (a single
 * flipped bit in flight is far more likely than a scrambled header), so it's surfaced as a
 * status flag rather than an exception.
 *
 * @throws Error if sync bytes invalid, size mismatch, or data is malformed
 */
export function decodeDevMessageWithCrcStatus(data: Uint8Array): DevMessageDecodeResult {
  // Validate minimum frame size (header + at least CRC32)
  if (data.length < HEADER_SIZE + 4) {
    throw new Error(
      `Frame too short: received ${data.length} bytes, minimum ${HEADER_SIZE + 4} bytes required`
    );
  }

  // Verify sync bytes
  if (data[0] !== SYNC_BYTE_1 || data[1] !== SYNC_BYTE_2) {
    throw new Error(
      `Invalid sync bytes: expected [0x${SYNC_BYTE_1.toString(16)}, 0x${SYNC_BYTE_2.toString(16)}], ` +
      `received [0x${data[0].toString(16)}, 0x${data[1].toString(16)}]`
    );
  }

  // Extract size (2 bytes, little-endian)
  const expectedPayloadSize = data[2] | (data[3] << 8);

  // Validate frame length matches declared size
  const actualPayloadSize = data.length - HEADER_SIZE;
  if (actualPayloadSize !== expectedPayloadSize) {
    throw new Error(
      `Frame size mismatch: header declares ${expectedPayloadSize} bytes, ` +
      `but received ${actualPayloadSize} bytes`
    );
  }

  // Extract payload (skip header)
  const payload = data.slice(HEADER_SIZE);

  // Verify CRC32 and extract protobuf data — mismatch is reported, not thrown
  const { valid, payload: protobufData, receivedCrc, calculatedCrc } = verifyCRC32(payload);

  const message = decodeProtobufPayload(protobufData);

  return { message, crcValid: valid, receivedCrc, calculatedCrc };
}

/**
 * Decodes a DevMessage from Uint8Array received via WebUSB
 *
 * @throws Error if sync bytes invalid, size mismatch, CRC check fails, or data is malformed
 */
export function decodeDevMessage(data: Uint8Array): DevMessage {
  const result = decodeDevMessageWithCrcStatus(data);

  if (!result.crcValid) {
    throw new Error(
      `CRC32 mismatch: received 0x${result.receivedCrc.toString(16).padStart(8, '0')}, ` +
        `calculated 0x${result.calculatedCrc.toString(16).padStart(8, '0')}`
    );
  }

  return result.message;
}

/**
 * Validate that the device acknowledged the sent command correctly
 * 
 * The device echoes back the received command in the app_message field.
 * This function compares the sent and received messages to detect communication errors.
 * 
 * @param sentMessage - The AppMessage that was sent to the device
 * @param receivedMessage - The DevMessage received from the device
 * @returns true if acknowledgement is valid, false if mismatch detected
 */
/**
 * Note: the device uses a request/response model — it does NOT echo AppMessage back.
 * The DevMessage.app_message field will always be absent.
 * This function is kept as a no-op in case future firmware adds echo support.
 */
export function validateAcknowledgement(
  _sentMessage: AppMessage,
  _receivedMessage: DevMessage
): boolean {
  return true;
}

/**
 * Decodes a DevMessage without CRC verification (for testing or raw data)
 * @param data - Raw protobuf bytes without CRC
 * @returns Decoded DevMessage
 */
export function decodeDevMessageRaw(data: Uint8Array): DevMessage {
  return DevMessage.decode(data);
}

/**
 * Safely decode DevMessage, returning null on failure
 * @param data - Raw bytes from USB transfer (with CRC32)
 * @returns Decoded DevMessage or null if invalid
 */
export function tryDecodeDevMessage(data: Uint8Array): DevMessage | null {
  try {
    return decodeDevMessage(data);
  } catch {
    return null;
  }
}

/**
 * Safely decode DevMessage while surfacing CRC status instead of discarding a frame whose CRC
 * failed but whose protobuf payload still parses. Returns null only for framing/protobuf
 * failures (bad sync, size mismatch, undecodable payload) — the same unrecoverable cases
 * `tryDecodeDevMessage` returns null for.
 * @param data - Raw bytes from USB transfer (with CRC32)
 * @returns Decode result (with crcValid flag) or null if unrecoverable
 */
export function tryDecodeDevMessageWithCrcStatus(data: Uint8Array): DevMessageDecodeResult | null {
  try {
    return decodeDevMessageWithCrcStatus(data);
  } catch {
    return null;
  }
}

// Module-level counter — resets on page reload (session scope is sufficient)
let _messageIdCounter = 1;

/**
 * Creates a new AppMessage with a unique sequential ID and current timestamp.
 * @param trigger - Control command to send
 * @param impedanceExec - Optional measurement configuration
 * @returns Complete AppMessage ready for encoding
 */
export function createAppMessage(
  trigger: Triggers,
  impedanceExec?: ImpedanceExec
): AppMessage {
  const now = Date.now();
  return {
    id: BigInt(_messageIdCounter++),
    timestampMs: BigInt(now),
    trig: trigger,
    impedanceExec,
  };
}

/**
 * Create a measurement command message
 * @param impedanceExec - Measurement configuration
 * @returns AppMessage configured for measurement
 */
export function createMeasurementMessage(impedanceExec: ImpedanceExec): AppMessage {
  return createAppMessage(Triggers.MEASURE_START, impedanceExec);
}

/**
 * Create a blink (device locator) command
 * @returns AppMessage to trigger LED blink
 */
export function createBlinkMessage(): AppMessage {
  return createAppMessage(Triggers.BLINK);
}

/**
 * Create a status request (no operation)
 * @returns AppMessage to request current state
 */
export function createStatusMessage(): AppMessage {
  return createAppMessage(Triggers.NONE);
}

// ============ UID Conversion ============

/**
 * Converts DevMessage.uid (12 bytes) to hex string for display
 * @param uid - Raw UID bytes
 * @returns Formatted hex string (e.g., "01:23:45:67:89:AB:CD:EF:12:34:56:78")
 */
export function uidToString(uid: Uint8Array): string {
  return Array.from(uid)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(':')
    .toUpperCase();
}

/**
 * Converts UID to short format (last 6 bytes)
 * @param uid - Raw UID bytes
 * @returns Short hex string (e.g., "12:34:56:78:9A:BC")
 */
export function uidToShortString(uid: Uint8Array): string {
  const last6 = uid.slice(-6);
  return Array.from(last6)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(':')
    .toUpperCase();
}

/**
 * Converts UID to "0x" prefixed format for backend
 * @param uid - Raw UID bytes
 * @returns Hex string with 0x prefix (e.g., "0x0123456789ABCDEF12345678")
 */
export function uidToHexString(uid: Uint8Array): string {
  return (
    '0x' +
    Array.from(uid)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()
  );
}

// ============ Version Conversion ============

/**
 * Extracts firmware version as string from Version
 * @param version - Version object from DevMessage
 * @returns Formatted version string (e.g., "1.2.3")
 */
export function getFirmwareVersion(version: Version | undefined): string {
  if (!version) return 'Unknown';

  const major = (version.firmware >> 16) & 0xff;
  const minor = (version.firmware >> 8) & 0xff;
  const patch = version.firmware & 0xff;
    
  return `${major}.${minor}.${patch}`;
}

/**
 * Extracts hardware version as string from Version
 * @param version - Version object from DevMessage
 * @returns Formatted version string (e.g., "2.0")
 */
export function getHardwareVersion(version: Version | undefined): string {
  if (!version) return 'Unknown';

  const major = (version.hardware >> 16) & 0xff;
  const minor = (version.hardware >> 8) & 0xff;
  const patch = version.hardware & 0xff;

  return `${major}.${minor}.${patch}`;
}

/**
 * Extracts protocol version as string from Version (diagnostics only; never branch on it)
 * @param version - Version object from DevMessage
 * @returns Formatted version string (e.g., "1.5.1")
 */
export function getProtocolVersion(version: Version | undefined): string {
  if (!version) return 'Unknown';

  const major = (version.protocol >> 16) & 0xff;
  const minor = (version.protocol >> 8) & 0xff;
  const patch = version.protocol & 0xff;

  return `${major}.${minor}.${patch}`;
}

/**
 * Get full version info as structured object
 * @param version - Version object from DevMessage
 */
export function getVersionInfo(version: Version | undefined) {
  return {
    firmware: getFirmwareVersion(version),
    hardware: getHardwareVersion(version),
    protocol: getProtocolVersion(version),
    bleStack: version?.bluetooth ?? 0,
  };
}

// ============ Error Handling ============

/**
 * Checks if DevMessage indicates an error state
 */
export function hasError(devMessage: DevMessage): boolean {
  return devMessage.errorCode !== 0;
}

/**
 * Decodes a packed device error code into its module and code components.
 *
 * As of firmware v1.4.4, `DevMessage.error_code` is a packed value:
 * `(module << 16) | code`, not a flat code. `module` identifies the firmware
 * subsystem that raised the error (1 = request/framing, 2 = validation,
 * 3 = measurement execution, ...); `code` is the module-specific error code.
 *
 * @param errorCode - Raw packed error code from the device
 * @returns The unpacked `{ module, code }` pair
 */
export function decodeErrorCode(errorCode: number): { module: number; code: number } {
  return {
    module: errorCode >>> 16,
    code: errorCode & 0xffff,
  };
}

/**
 * Gets human-readable error message from a packed error code
 */
export function getErrorMessage(errorCode: number): string {
  if (errorCode === 0) return 'No error';

  const { module, code } = decodeErrorCode(errorCode);
  return `Module ${module} error 0x${code.toString(16)}`;
}

/**
 * Determine if an error is recoverable — i.e. whether the response carrying it
 * still holds trustworthy data (e.g. a partial `impedanceResult`) worth using
 * rather than discarding outright.
 *
 * The only recoverable case today is a `MEASURE_ABORT`-interrupted sweep:
 * module 3 (measurement execution), code 0x02. Every other nonzero code —
 * including any other module 3 code — is treated as non-recoverable.
 *
 * @param errorCode - Raw packed error code from the device
 * @returns true if the response carrying this error still holds usable data
 */
export function isRecoverableError(errorCode: number): boolean {
  const { module, code } = decodeErrorCode(errorCode);
  return module === 3 && code === 0x02;
}

// ============ State Helpers ============

/**
 * Checks if measurement is complete based on progress
 */
export function isMeasurementComplete(state: State | undefined): boolean {
  if (!state) return false;
  return state.processingId === 0n && state.progressPct >= 100;
}

/**
 * Checks if device is currently measuring
 * @param state - Device state from DevMessage
 * @returns true if device is busy
 */
export function isMeasuring(state: State | undefined): boolean {
  return (state?.processingId ?? 0n) > 0n;
}

/**
 * Converts PowerState enum to human-readable string
 */
export function getPowerStateString(powerState: PowerState): string {
  const states: Record<PowerState, string> = {
    [PowerState.UNKNOWN]: 'Unknown',
    [PowerState.BATTERY]: 'Running on Battery',
    [PowerState.CHARGING]: 'Charging',
    [PowerState.STANDBY]: 'Fully Charged',
    [PowerState.FAULT]: 'Charging Fault',
    [PowerState.NO_BATTERY]: 'No Battery',
    [PowerState.UNRECOGNIZED]: 'Unrecognized',
  };
  return states[powerState] || 'Unknown';
}

/**
 * Converts ElectrodeDetect enum to human-readable string
 *
 * v1.4.4's CODE_1–CODE_7 have no known physical electrode mapping yet (blocked
 * on hardware/EE); they all surface as an explicit "Unrecognized" state rather
 * than a guessed channel-count label.
 */
export function getElectrodeTypeString(electrode: ElectrodeDetect): string {
  const types: Record<ElectrodeDetect, string> = {
    [ElectrodeDetect.NC]: 'Not Connected',
    [ElectrodeDetect.CODE_1]: 'Unrecognized',
    [ElectrodeDetect.CODE_2]: 'Unrecognized',
    [ElectrodeDetect.CODE_3]: 'Unrecognized',
    [ElectrodeDetect.CODE_4]: 'Unrecognized',
    [ElectrodeDetect.CODE_5]: 'Unrecognized',
    [ElectrodeDetect.CODE_6]: 'Unrecognized',
    [ElectrodeDetect.CODE_7]: 'Unrecognized',
    [ElectrodeDetect.UNRECOGNIZED]: 'Unrecognized',
  };
  return types[electrode] || 'Unknown';
}

/**
 * Check if device has low battery
 * @param state - Device state
 * @param threshold - Warning threshold (default 20%)
 * @returns true if battery below threshold
 */
export function hasLowBattery(state: State | undefined, threshold = 20): boolean {
  return (state?.batteryPct ?? 100) < threshold;
}

// ============ Impedance Result Helpers ============

/**
 * Extract impedance data as arrays for charting
 * @param result - Impedance result from measurement
 * @param channelIndex - Which entry of `result.data[]` to read (default 0)
 * @returns Object with frequencies, magnitudes, and phases as arrays
 */
export function extractImpedanceData(
  result: ImpedanceResult | undefined,
  channelIndex = 0
) {
  const channel = result?.data?.[channelIndex];
  if (!result?.frequencyHz || !channel) {
    return { frequencies: [] as number[], magnitudes: [] as number[], phases: [] as number[] };
  }

  // A MEASURE_ABORT-interrupted channel can legitimately report fewer points than
  // frequencyHz (proto's own documented behavior). Truncate to the shortest array
  // rather than returning mismatched lengths a caller would zip by index — better
  // an incomplete-but-consistent result than undefined entries past the short end.
  const length = Math.min(result.frequencyHz.length, channel.magnitudeOhm.length, channel.phaseRad.length);
  if (length !== result.frequencyHz.length || length !== channel.magnitudeOhm.length || length !== channel.phaseRad.length) {
    logger.warn(
      '[extractImpedanceData] channel array length mismatch, truncating',
      { frequencyHz: result.frequencyHz.length, magnitudeOhm: channel.magnitudeOhm.length, phaseRad: channel.phaseRad.length, channelIndex }
    );
  }

  return {
    frequencies: result.frequencyHz.slice(0, length),
    magnitudes: channel.magnitudeOhm.slice(0, length),
    phases: channel.phaseRad.slice(0, length),
  };
}

/**
 * Get impedance at specific frequency
 * @param result - Impedance result
 * @param frequency - Target frequency in Hz
 * @param channelIndex - Which entry of `result.data[]` to read (default 0)
 * @returns The magnitude/phase pair at that frequency, or undefined if not found
 */
export function getImpedanceAtFrequency(
  result: ImpedanceResult | undefined,
  frequency: number,
  channelIndex = 0
): { magnitudeOhm: number; phaseRad: number } | undefined {
  const channel = result?.data?.[channelIndex];
  if (!result?.frequencyHz || !channel) return undefined;

  const index = result.frequencyHz.findIndex((f) => Math.abs(f - frequency) < 0.1);
  if (index < 0) return undefined;

  const magnitudeOhm = channel.magnitudeOhm[index];
  const phaseRad = channel.phaseRad[index];
  if (magnitudeOhm === undefined || phaseRad === undefined) return undefined;

  return { magnitudeOhm, phaseRad };
}

/**
 * Find frequency with minimum impedance magnitude
 * @param result - Impedance result
 * @param channelIndex - Which entry of `result.data[]` to read (default 0)
 * @returns Object with frequency and impedance value
 */
export function findMinImpedance(result: ImpedanceResult | undefined, channelIndex = 0) {
  const channel = result?.data?.[channelIndex];
  if (!result?.frequencyHz?.length || !channel?.magnitudeOhm?.length) {
    return { frequency: 0, magnitude: 0, phase: 0 };
  }

  let minIndex = 0;
  let minMagnitude = channel.magnitudeOhm[0];

  for (let i = 1; i < channel.magnitudeOhm.length; i++) {
    if (channel.magnitudeOhm[i] < minMagnitude) {
      minMagnitude = channel.magnitudeOhm[i];
      minIndex = i;
    }
  }

  return {
    frequency: result.frequencyHz[minIndex],
    magnitude: channel.magnitudeOhm[minIndex],
    phase: channel.phaseRad[minIndex],
  };
}

// ============ Channel Helpers ============

/**
 * Get channel name for display
 * @param channel - CHAN enum value
 * @returns Display name
 */
export function getChannelName(channel: CHAN): string {
  const names: Record<CHAN, string> = {
    [CHAN.OPEN]: 'Open',
    [CHAN.CH1]: 'Channel 1',
    [CHAN.CH2]: 'Channel 2',
    [CHAN.CH3]: 'Channel 3',
    [CHAN.CH4]: 'Channel 4',
    [CHAN.CAL]: 'CAL',
    [CHAN.UNRECOGNIZED]: 'Unknown',
  };
  return names[channel] || 'Unknown';
}

/**
 * Get TIA resistor value in ohms
 * @param tia - TIA enum value
 * @returns Resistance in ohms
 */
export function getTiaResistance(tia: TIA): number {
  const num = tIAToNumber(tia);
  if (num < 0) return 0;
  const values = [200, 1000, 5000, 10000, 20000, 40000, 80000, 160000];
  return values[num] ?? 0;
}

// ============ Re-exports for convenience ============

export type {
  AppMessage,
  DevMessage,
  ImpedanceExec,
  ImpedanceResult,
  ImpedanceData,
  State,
  Version,
  Scaler,
};

export { Triggers, PowerState, ElectrodeDetect, CHAN, TIA, ADC };